import { html, ref, repeat } from '@microsoft/fast-element';
import { AnalyticsStepOne, PREFERENCES_PRODUCT_TYPE } from './analytics-step-one';
import { sync } from '@genesislcap/foundation-utils';
import { whiteCheckIcon, whiteErrorIcon } from '../../../../styles';
import { eventDetail } from '../../../../utils/formatting';
import { PRODUCT_TYPE } from '../../../../utils';

export const AnalyticsStepOneTemplate = html<AnalyticsStepOne>`
  <div class="step-one-wrapper">
    <zero-card class="preferences-wrapper no-padding-card">
      <zero-tabs class="horizontal" orientation="horizontal">
        <span slot="start" class="tab-title">Set Preferences</span>
      </zero-tabs>

      <div class="description">
        Please weigh the contract metrics in accordance with your preferences.
        <br />
        <br />
        CleanTrade will weigh the results in accordance with your preferences and produce a ranked
        listing of all available contracts.
        <br />
        <br />
        <span class="explanation">(0 = Not Relevant; 4 = Most Relevant)</span>
      </div>
      <div class="controls-wrapper">
        <div class="form-control">
          <label class="form-label">Product Type</label>
          <zero-select
            class="input-control"
            :value=${sync((x) => x.productTypeValue)}
            ${ref('productTypeElement')}
          >
            ${repeat(
              (x) => x.productTypes,
              html`
                <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
              `
            )}
          </zero-select>
        </div>

        <div class="form-control">
          <label class="form-label">Applicable REC Program</label>
          <zero-select
            class="input-control"
            :value=${sync((x) => x.applicableRECProgramValue)}
            ${ref('applicableRECProgramElement')}
          >
            ${repeat(
              (x) => x.applicableRECProgramTypes,
              html`
                <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
              `
            )}
          </zero-select>
        </div>

        <div class="form-control">
          <label class="form-label">Location</label>
          <zero-select
            class="input-control"
            :value=${sync((x) => x.locationDDValue)}
            ${ref('locationDDElement')}
          >
            ${repeat(
              (x) => x.iso,
              html`
                <zero-option value="${(x) => x}">${(x) => x}</zero-option>
              `
            )}
          </zero-select>
          <zero-segmented-control
            appearance="secondary"
            orientation="horizontal"
            :value="${sync((x) => x.location)}"
          >
            <zero-segmented-item value="0">0</zero-segmented-item>
            <zero-segmented-item value="1">1</zero-segmented-item>
            <zero-segmented-item value="2">2</zero-segmented-item>
            <zero-segmented-item value="3">3</zero-segmented-item>
            <zero-segmented-item value="4">4</zero-segmented-item>
          </zero-segmented-control>
        </div>

        <div class="form-control">
          <label class="form-label">Expected Settlement</label>
          <zero-segmented-control
            ${ref('expectedSettlementElement')}
            appearance="secondary"
            orientation="horizontal"
            :value="${sync((x) => x.expectedSettlement)}"
          >
            <zero-segmented-item value="0">0</zero-segmented-item>
            <zero-segmented-item value="1">1</zero-segmented-item>
            <zero-segmented-item value="2">2</zero-segmented-item>
            <zero-segmented-item value="3">3</zero-segmented-item>
            <zero-segmented-item value="4">4</zero-segmented-item>
          </zero-segmented-control>
        </div>
        <div class="form-control">
          <label class="form-label">Nodal LME - Historical</label>
          <zero-segmented-control
            appearance="secondary"
            orientation="horizontal"
            :value="${sync((x) => x.nodalLme)}"
          >
            <zero-segmented-item value="0">0</zero-segmented-item>
            <zero-segmented-item value="1">1</zero-segmented-item>
            <zero-segmented-item value="2">2</zero-segmented-item>
            <zero-segmented-item value="3">3</zero-segmented-item>
            <zero-segmented-item value="4">4</zero-segmented-item>
          </zero-segmented-control>
        </div>
        <div class="form-control">
          <label class="form-label">Implied Carbon Price</label>
          <zero-segmented-control
            appearance="secondary"
            orientation="horizontal"
            :value="${sync((x) => x.impliedCarbonPrice)}"
          >
            <zero-segmented-item value="0">0</zero-segmented-item>
            <zero-segmented-item value="1">1</zero-segmented-item>
            <zero-segmented-item value="2">2</zero-segmented-item>
            <zero-segmented-item value="3">3</zero-segmented-item>
            <zero-segmented-item value="4">4</zero-segmented-item>
          </zero-segmented-control>
        </div>
        <div class="form-control">
          <label class="form-label">Project Additionality</label>
          <zero-segmented-control
            ${ref('additionalityElement')}
            appearance="secondary"
            orientation="horizontal"
            :value="${sync((x) => x.additionality)}"
          >
            <zero-segmented-item value="0">0</zero-segmented-item>
            <zero-segmented-item value="1">1</zero-segmented-item>
            <zero-segmented-item value="2">2</zero-segmented-item>
            <zero-segmented-item value="3">3</zero-segmented-item>
            <zero-segmented-item value="4">4</zero-segmented-item>
          </zero-segmented-control>
        </div>
      </div>
      <div class="card-footer">
        <div class="footer-logo">
          Metrics powered by
          <div class="resurety-logo"></div>
        </div>
        <zero-button appearance="secondary" @click="${(x) => x.resetPreferences()}">
          ${whiteErrorIcon('start')}Reset
        </zero-button>
        <zero-button
          appearance="primary"
          ?disabled=${(x) => x.disabledConfirmButton}
          @click="${(x) => x.confirmReset()}"
        >
          ${whiteCheckIcon('start')}Confirm
        </zero-button>
      </div>
    </zero-card>
    <zero-card class="all-contracts-wrapper no-padding-card">
      <zero-tabs class="horizontal" orientation="horizontal">
        <span slot="start" class="tab-title">All Contracts</span>
      </zero-tabs>
      <all-contracts-graph
        @enableProceedButton="${(x, c) => x.proceedToDrilldown(eventDetail(c))}"
      ></all-contracts-graph>
    </zero-card>
    <zero-card class="quadrant-wrapper no-padding-card">
      <zero-tabs class="horizontal" orientation="horizontal">
        <span slot="start" class="tab-title">Quadrant</span>
      </zero-tabs>
      <div class="content-wrapper">
        <quadrant-spider-chart></quadrant-spider-chart>
        <quadrant-grid ${ref('quadrantGrid')}></quadrant-grid>
      </div>
    </zero-card>
  </div>
`;
